"use client";

import { ImgHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { FALLBACK_LOGO_IMAGE } from "@/constant";
import { IMAGE_POSTFIX_MOBILECARD, IMAGE_POSTFIX_WEBCARD, IMAGE_POSTFIX_WEBFULL } from "commonModule/globalConstant";
const ProgressiveImage = ({
  src,
  alt,
  postfix = IMAGE_POSTFIX_WEBCARD,
  highResPostfix = IMAGE_POSTFIX_WEBFULL,
  fallbackSrc = FALLBACK_LOGO_IMAGE,
  progressiveLoadingAggressiveness = "onIdleAndHover",
  ...props
}: ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  alt: string;
  postfix?: typeof IMAGE_POSTFIX_WEBCARD | typeof IMAGE_POSTFIX_WEBFULL | typeof IMAGE_POSTFIX_MOBILECARD | null;
  highResPostfix?: typeof IMAGE_POSTFIX_WEBCARD | typeof IMAGE_POSTFIX_WEBFULL | typeof IMAGE_POSTFIX_MOBILECARD | "original" | null;
  fallbackSrc?: string;
  progressiveLoadingAggressiveness?: "onHoverOnly" | "onIdleAndHover";
  className?: string;
}) => {
  const [imageSrc, setImageSrc] = useState(postfix ? `${src}_${postfix}` : src);
  const [highResLoaded, setHighResLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const loadHighRes = useCallback(() => {
    if (highResLoaded || hasError) return;
    if (!highResPostfix) {
      setHighResLoaded(true);
      return;
    }
    const highResSrc = highResPostfix !== "original" ? `${src}_${highResPostfix}` : src;
    const img = new Image();
    img.src = highResSrc;
    img.onload = () => {
      setImageSrc(highResSrc);
      setHighResLoaded(true);
    };
    img.onerror = () => {
      setHasError(true);
      setHighResLoaded(true);
      if (fallbackSrc) {
        setImageSrc(fallbackSrc);
      }
    };
  }, [fallbackSrc, hasError, highResLoaded, highResPostfix, src]);
  const handleError = () => {
    if (!highResLoaded) {
      const highResSrc = src;
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setImageSrc(highResSrc);
        setHighResLoaded(true);
      };
      img.onerror = () => {
        setHasError(true);
        setHighResLoaded(true);
        if (fallbackSrc) {
          setImageSrc(fallbackSrc);
        }
      };
    }
    if (fallbackSrc && !hasError) {
      setImageSrc(fallbackSrc);
      setHasError(true);
    }
  };
  useEffect(() => {
    if (progressiveLoadingAggressiveness !== "onIdleAndHover") {
      return;
    }

    // Determine if it's a mobile device
    const isMobileDevice = window.innerWidth < 768;
    if (isMobileDevice) {
      // If mobile device, load high-res only on hover
      return;
    }

    // Detect slow network
    let isSlowNetwork = false;
    if ("connection" in navigator) {
      const conn = navigator.connection;
      if (conn &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/dot-notation
      ["3g", "2g", "slow-2g"].includes(conn["effectiveType"] || "")) {
        isSlowNetwork = true;
      }
    }
    if (isSlowNetwork) {
      // If slow network, load high-res only on hover
      return;
    }
    if (!imgRef.current) {
      return;
    }

    // Try loading high-res when the image is in view
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }
        if ("requestIdleCallback" in window) {
          requestIdleCallback(loadHighRes, {
            timeout: 2000
          });
        } else {
          setTimeout(loadHighRes, 3000);
        }
        observer.disconnect();
      });
    }, {
      threshold: 0.1
    });
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [loadHighRes, progressiveLoadingAggressiveness]);
  return <img ref={imgRef} src={imageSrc || src} alt={alt} onError={handleError} onMouseEnter={loadHighRes} onTouchStart={loadHighRes} {...props} data-sentry-component="ProgressiveImage" data-sentry-source-file="ProgressiveImage.tsx" />;
};
export default ProgressiveImage;